import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Gavel, Truck, KeyRound, Briefcase, Handshake, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CATEGORIES, CITIES, PERIODS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ImagePicker } from "@/components/ImagePicker";
import { CompanyDetailsForm, EMPTY_COMPANY, cleanCompany } from "@/components/CompanyDetails";
import { FinancialsForm, EMPTY_FIN, cleanFin } from "@/components/Financials";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const TYPES = [
  { v: "auction", l: "مزاد علني", icon: Gavel }, { v: "sale", l: "بيع مباشر", icon: Truck },
  { v: "rent", l: "إيجار", icon: KeyRound }, { v: "job", l: "وظيفة", icon: Briefcase },
  { v: "invest", l: "استثمار وشراكة", icon: Handshake },
];

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="font-semibold">{label}</Label>{children}</div>;
const Sel = ({ value, onChange, children, testId }) => <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-xl border px-3 bg-white text-sm">{children}</select>;

const TypeFields = ({ f, set }) => {
  if (f.type === "auction") return (
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="سعر الافتتاح (ر.س)"><Input data-testid="input-start-price" type="number" value={f.start_price} onChange={(e) => set("start_price", e.target.value)} className="h-11 rounded-xl" /></Field>
      <Field label="أقل زيادة (ر.س)"><Input data-testid="input-min-increment" type="number" value={f.min_increment} onChange={(e) => set("min_increment", e.target.value)} placeholder="تلقائي" className="h-11 rounded-xl" /></Field>
      <Field label="مدة المزاد"><Sel testId="input-duration" value={f.duration_days} onChange={(v) => set("duration_days", v)}>{[1, 3, 5, 7, 14, 30].map((d) => <option key={d} value={d}>{d} يوم</option>)}</Sel></Field>
    </div>
  );
  if (f.type === "job") return (
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="اسم الشركة"><Input data-testid="input-company" value={f.company} onChange={(e) => set("company", e.target.value)} className="h-11 rounded-xl" /></Field>
      <Field label="نوع الدوام"><Sel testId="input-job-type" value={f.job_type} onChange={(v) => set("job_type", v)}>{["دوام كامل", "دوام جزئي", "عقد مؤقت", "عن بعد"].map((t) => <option key={t}>{t}</option>)}</Sel></Field>
      <Field label="الراتب"><Input data-testid="input-salary" value={f.salary} onChange={(e) => set("salary", e.target.value)} placeholder="مثال: 6,000 ريال" className="h-11 rounded-xl" /></Field>
    </div>
  );
  if (f.type === "invest") return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="المبلغ المطلوب (ر.س)"><Input data-testid="input-price" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="h-11 rounded-xl" /></Field>
      <Field label="نسبة الحصة المعروضة %"><Input data-testid="input-stake-percent" type="number" min={1} max={100} value={f.stake_percent} onChange={(e) => set("stake_percent", e.target.value)} className="h-11 rounded-xl" /></Field>
    </div>
  );
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="السعر (ر.س)"><Input data-testid="input-price" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="h-11 rounded-xl" /></Field>
      {f.type === "rent" && <Field label="فترة الإيجار"><Sel testId="input-rent-period" value={f.rent_period} onChange={(v) => set("rent_period", v)}>{Object.entries(PERIODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Sel></Field>}
      <Field label="سنة الصنع"><Input data-testid="input-year" type="number" value={f.year} onChange={(e) => set("year", e.target.value)} className="h-11 rounded-xl" /></Field>
    </div>
  );
};

const num = (v) => (v === "" || v == null ? null : Number(v));

export default function AddListing() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const init = params.get("type") || "auction";
  const [f, setF] = useState({ type: init, category: CATEGORIES[init][0].v, title: "", description: "", city: CITIES[0], image: null,
    price: "", rent_period: "month", start_price: "", min_increment: "", duration_days: 7, company: user?.company_name || "", job_type: "دوام كامل", salary: "", year: "", contact_phone: user?.phone || "", company_details: EMPTY_COMPANY, financials: EMPTY_FIN, stake_percent: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setType = (t) => setF((p) => ({ ...p, type: t, category: CATEGORIES[t][0].v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, price: num(f.price), start_price: num(f.start_price), min_increment: num(f.min_increment), duration_days: num(f.duration_days), year: num(f.year), stake_percent: f.type === "invest" ? num(f.stake_percent) : null, rent_period: f.type === "rent" ? f.rent_period : null };
      const corp = f.category === "commercial_registration" || f.type === "invest";
      body.financials = corp ? cleanFin(f.financials) : null;
      if (f.type === "job") body.image = null;
      else { body.company = null; body.job_type = null; body.salary = null; }
      body.company_details = (f.category === "commercial_registration" || f.type === "invest") ? cleanCompany(f.company_details) : null;
      await api.post("/listings", body);
      toast.success("تم إرسال الإعلان لمراجعة الإدارة");
      nav("/dashboard");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="add-listing-page">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">أضف إعلاناً جديداً</h1>
      <p className="text-slate-500 mb-8 flex items-center gap-1.5"><Info size={16} />يظهر الإعلان بعد مراجعة واعتماد الإدارة.</p>
      {!user.is_admin && user.role !== "seller" && (
        <div className="bg-amber-50 text-amber-800 rounded-xl p-4 mb-6 text-sm" data-testid="add-not-seller-notice">
          إضافة الإعلانات متاحة لحسابات البائعين فقط. <Link to="/dashboard" className="font-bold underline">غيّر نوع حسابك إلى بائع</Link>
        </div>
      )}
      {!user.is_admin && user.verification?.status !== "verified" && (
        <div className="bg-amber-50 text-amber-800 rounded-xl p-4 mb-6 text-sm" data-testid="add-unverified-notice">
          يجب توثيق حسابك بالسجل التجاري قبل إضافة الإعلانات. <Link to="/dashboard" className="font-bold underline" data-testid="add-go-verify">اذهب للتوثيق</Link>
        </div>
      )}
      <form onSubmit={submit} className="bg-white border rounded-xl p-6 sm:p-8 space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {TYPES.map((t) => (
            <button type="button" key={t.v} data-testid={`type-${t.v}`} onClick={() => setType(t.v)}
              className={`rounded-xl border-2 p-4 flex flex-col items-center gap-2 font-bold text-sm transition-colors ${f.type === t.v ? "border-brand bg-brand/10 text-brand" : "hover:border-slate-300"}`}>
              <t.icon size={20} />{t.l}
            </button>
          ))}
        </div>
        {f.type === "auction" && <div className="bg-[#FFF7ED] text-[#C2410C] rounded-xl p-3 text-sm font-semibold" data-testid="add-commission-note">عمولة الموقع 1% من سعر البيع النهائي. يبدأ العد التنازلي عند اعتماد الإدارة.</div>}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="التصنيف"><Sel testId="input-category" value={f.category} onChange={(v) => set("category", v)}>{CATEGORIES[f.type].map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</Sel></Field>
          <Field label="المدينة"><Sel testId="input-city" value={f.city} onChange={(v) => set("city", v)}>{CITIES.map((c) => <option key={c}>{c}</option>)}</Sel></Field>
        </div>
        <Field label={f.type === "job" ? "المسمى الوظيفي" : "عنوان الإعلان"}><Input data-testid="input-title" required minLength={3} value={f.title} onChange={(e) => set("title", e.target.value)} className="h-11 rounded-xl" /></Field>
        <Field label="الوصف"><Textarea data-testid="input-description" rows={5} value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" /></Field>
        <TypeFields f={f} set={set} />
        {(f.category === "commercial_registration" || f.type === "invest") && <CompanyDetailsForm c={f.company_details} set={(v) => set("company_details", v)} />}
        {(f.category === "commercial_registration" || f.type === "invest") && <FinancialsForm v={f.financials} set={(v) => set("financials", v)} />}
        {f.type !== "job" && <Field label="صورة"><ImagePicker value={f.image} onChange={(v) => set("image", v)} /></Field>}
        <Field label="رقم التواصل (مخفي حتى موافقة الإدارة)"><Input data-testid="input-contact-phone" required minLength={6} value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} dir="ltr" className="h-11 rounded-xl text-right" placeholder="05XXXXXXXX" /></Field>
        <Button data-testid="submit-listing-btn" disabled={busy} type="submit" className="w-full h-12 rounded-lg bg-brand hover:bg-brand-dark text-base font-bold">إرسال للمراجعة</Button>
      </form>
    </div>
  );
}
